import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

const ERROR_QUEUE_KEY = 'team_v_error_queue';
const DEVICE_ID_KEY = 'team_v_device_id';
const SYNC_INTERVAL = 30000; // 30 seconds
const MAX_QUEUE_SIZE = 50;

// Global state for error context (since logger is outside React context)
export let currentErrorUserId: string | null = null;
export let currentErrorTeamId: string | null = null;

export const setErrorContext = (userId: string | null, teamId: string | null) => {
  currentErrorUserId = userId;
  currentErrorTeamId = teamId;
};

interface ErrorLogEntry {
  error_message: string;
  error_stack?: string;
  error_type?: string;
  url?: string;
  user_agent?: string;
  metadata?: Json;
  created_at: string;
}

// Get queued errors from localStorage
function getErrorQueue(): ErrorLogEntry[] {
  try {
    const queue = localStorage.getItem(ERROR_QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch {
    return [];
  }
}

// Save error queue to localStorage
function saveErrorQueue(queue: ErrorLogEntry[]): void {
  try {
    // Limit queue size
    const limitedQueue = queue.slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(limitedQueue));
  } catch {
    // Storage might be full, clear old entries
    localStorage.removeItem(ERROR_QUEUE_KEY);
  }
}

// Sanitize URLs to remove sensitive tokens before logging
function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const sensitiveParams = ['__lovable_token', 'token', 'access_token', 'refresh_token', 'code', 'state'];
    sensitiveParams.forEach(param => urlObj.searchParams.delete(param));
    return urlObj.toString();
  } catch {
    return '[invalid URL]';
  }
}

// Add error to queue
export function logError(
  error: Error | string,
  metadata?: Record<string, unknown>
): void {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorStack = typeof error === 'string' ? undefined : error.stack;
  const errorType = typeof error === 'string' ? 'string' : error.name;

const entry: ErrorLogEntry = {
    error_message: errorMessage,
    error_stack: errorStack?.slice(0, 2000),
    error_type: errorType,
    url: sanitizeUrl(window.location.href),
    user_agent: navigator.userAgent,
    metadata: {
      ...(typeof metadata === 'object' && metadata !== null ? metadata : {}),
      build: __APP_BUILD__,
      user_id: currentErrorUserId,
      team_id: currentErrorTeamId,
    } as Json,
    created_at: new Date().toISOString(),
  };

  const queue = getErrorQueue();
  queue.push(entry);
  saveErrorQueue(queue);

  // Also log to console for development
  console.error('[ErrorLog]', errorMessage, metadata);
}

// Sync errors to backend
async function syncErrors(): Promise<void> {
  const queue = getErrorQueue();
  if (queue.length === 0) return;

  const deviceId = localStorage.getItem(DEVICE_ID_KEY);

  try {
    const entries = queue.map(entry => ({
      ...entry,
      device_id: deviceId || null,
    }));

    const { error } = await supabase
      .from('device_error_logs')
      .insert(entries);

    if (!error) {
      // Clear queue on success
      localStorage.removeItem(ERROR_QUEUE_KEY);
    }
  } catch (e) {
    // Silent fail - we'll retry on next sync
    console.debug('[ErrorLog] Sync failed, will retry', e);
  }
}

export function useErrorLogging() {
  const syncIntervalRef = useRef<number | null>(null);

  // Global error handler
  const handleGlobalError = useCallback((event: ErrorEvent) => {
    logError(event.error || event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  }, []);

  // Unhandled promise rejection handler
  const handleUnhandledRejection = useCallback((event: PromiseRejectionEvent) => {
    const error = event.reason;
    logError(
      error instanceof Error ? error : String(error),
      { type: 'unhandled_promise_rejection' }
    );
  }, []);

  useEffect(() => {
    // Set up global error handlers
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Initial sync on mount
    syncErrors();

    // Set up periodic sync
    syncIntervalRef.current = window.setInterval(syncErrors, SYNC_INTERVAL);

    // Sync on page unload
    const handleBeforeUnload = () => {
      syncErrors();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [handleGlobalError, handleUnhandledRejection]);

  return { logError, syncErrors };
}
